import { useInView, useMotionValue, useSpring } from 'framer-motion';
import { useEffect, useRef } from 'react';

interface CountUpProps {
  to: number;
  duration?: number;
  /** BCP 47 locale; when set, numbers are grouped (1,234). Unset keeps en-US with no grouping. */
  locale?: string;
}

const getDecimalPlaces = (num: number): number => {
  const decimals = num.toString().split('.')[1];
  return decimals ? decimals.length : 0;
};

const formatValue = (value: number, decimals: number, locale?: string): string =>
  value.toLocaleString(locale ?? 'en-US', {
    useGrouping: locale !== undefined,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });

export default function CountUp({ to, duration = 2, locale }: CountUpProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const motionValue = useMotionValue(0);
  const springValue = useSpring(motionValue, {
    damping: 20 + 40 * (1 / duration),
    stiffness: 100 * (1 / duration)
  });
  const isInView = useInView(ref, { once: true, margin: '0px' });

  const decimals = getDecimalPlaces(to);

  useEffect(() => {
    if (isInView) motionValue.set(to);
  }, [isInView, motionValue, to]);

  useEffect(
    () =>
      springValue.on('change', (latest: number) => {
        if (ref.current) ref.current.textContent = formatValue(latest, decimals, locale);
      }),
    [springValue, decimals, locale]
  );

  return <span ref={ref}>{formatValue(0, decimals, locale)}</span>;
}
